import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircleIcon, ExclamationCircleIcon } from '@nimbus-ds/icons'
import { authService } from '@/api/auth'
import { useAuth } from '@/hooks/useAuth'
import { Button, Card, Spinner, Title, Text } from '@nimbus-ds/components'

type CallbackStatus = 'loading' | 'success' | 'error'

export function AuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refreshAuth } = useAuth()
  const [status, setStatus] = useState<CallbackStatus>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for OAuth errors first
        const errorParam = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')

        if (errorParam) {
          throw new Error(errorDescription || `OAuth error: ${errorParam}`)
        }

        // Google SSO flow: ?access_token=...&refresh_token=...&flow=google
        const flow = searchParams.get('flow')
        const accessToken = searchParams.get('access_token')
        const refreshToken = searchParams.get('refresh_token')

        if (flow === 'google' && accessToken) {
          console.log('Processing Google SSO callback')
          authService.bootstrapGoogleSession(accessToken, refreshToken || '')
          // Sync AuthContext with the freshly-written localStorage so withAuth
          // sees isAuthenticated=true on the next route render (avoids the
          // window.location.href='/' bounce inside withAuth).
          await refreshAuth()
          setStatus('success')
          setTimeout(() => navigate('/dashboard', { replace: true }), 600)
          return
        }

        // Legacy support: Check for old-style direct success redirects
        const successParam = searchParams.get('success')

        if (successParam && accessToken) {
          console.log('Legacy OAuth redirect detected - redirecting to new auth-success page')

          // Redirect to the new auth-success page with the same parameters
          const params = new URLSearchParams(searchParams)
          navigate(`/auth-success?${params.toString()}`, { replace: true })
          return
        }

        // Traditional callback handling with code/state (for backward compatibility)
        const code = searchParams.get('code')
        const state = searchParams.get('state')

        if (code && state) {
          console.log('Processing traditional OAuth callback with code exchange')

          // Handle the OAuth callback via API
          const response = await authService.handleCallback(code, state)

          if (response.success && response.data) {
            // Store auth data and redirect to dashboard
            setStatus('success')
            setTimeout(() => {
              navigate('/dashboard')
            }, 1000)
          } else {
            throw new Error(response.error?.message || 'Authentication failed')
          }
          return
        }

        // No valid parameters found
        throw new Error('Invalid callback URL - missing required parameters')

      } catch (err) {
        console.error('OAuth callback error:', err)
        setError(err instanceof Error ? err.message : 'Authentication failed')
        setStatus('error')
      }
    }

    handleCallback()
  }, [searchParams, navigate, refreshAuth])

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Card className="w-full max-w-md">
            <Card.Header>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
                  <Spinner size="large" />
                </div>
                <Title as="h3">Autenticando...</Title>
                <Text>Procesando tu inicio de sesión</Text>
              </div>
            </Card.Header>
          </Card>
        )

      case 'success':
        return (
          <Card className="w-full max-w-md">
            <Card.Header>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckCircleIcon size={32} className="text-green-600" />
                </div>
                <Title as="h3" className="text-green-900">¡Autenticación Exitosa!</Title>
                <Text>Redirigiendo al dashboard...</Text>
              </div>
            </Card.Header>
          </Card>
        )

      case 'error':
        return (
          <Card className="w-full max-w-md">
            <Card.Header>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <ExclamationCircleIcon size={32} className="text-red-600" />
                </div>
                <Title as="h3" className="text-red-900">Error de Autenticación</Title>
                <Text className="text-red-700">
                  {error || 'No se pudo completar la autenticación'}
                </Text>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <div className="space-y-3">
                  <Button
                    appearance="primary"
                    onClick={() => navigate('/')}
                    fullWidth
                  >
                    Volver al Inicio
                  </Button>
                  <Button
                    appearance="neutral"
                    onClick={() => window.location.reload()}
                    fullWidth
                  >
                    Intentar Nuevamente
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        )
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--nimbus-colors-primary-surface)] to-white">
      <div className="flex min-h-screen items-center justify-center p-4">
        {renderContent()}
      </div>
    </div>
  )
}